import { GetAllLabWorksQuery, LabWork } from '@pm/graphql';
import { Card, IconTile, List, Text } from '@pm/ui';
import { type ListGrouping } from '@pm/ui/src/components/List/ListTypes';
import { useTranslation } from 'react-i18next';
import { LabListItem } from './LabListItem';

// Derived from the past query so LabsList can accept edges with or without observations.
// The new-labs query (which includes observations) is a structural superset and also satisfies this type.
export type LabsListEdge = NonNullable<
  GetAllLabWorksQuery['labWorks']
>['edges'][number];

type LabsListProps = {
  labWorks: LabsListEdge[];
  label: string;
  empty?: boolean;
  supportLink?: string;
  labFeedbackUrl?: string | null;
  isNew?: boolean;
};

export const LabsList = ({
  labWorks,
  label,
  labFeedbackUrl,
  empty = false,
  supportLink = '',
  isNew = false,
}: LabsListProps) => {
  const { t } = useTranslation('labs');
  const listGrouping: ListGrouping = isNew ? 'separated' : 'joined';

  if (labWorks.length === 0 && empty) {
    const noLabsCopy = isNew ? t('NoNewLabs') : t('NoPastLabs');
    return (
      <Card size="large">
        <div className="space-y-xs text-center">
          <IconTile intent="info" icon="labs" size="large" />
          <Text size="bodyS">{noLabsCopy}</Text>
        </div>
      </Card>
    );
  }

  const firstLabWork = labWorks[0]?.node.id;

  return (
    <div className="flex-col space-y-xxxl">
      <List
        interactionType="accordion"
        defaultValue={firstLabWork}
        type="single"
        aria-label={label}
        grouping={listGrouping}
      >
        {labWorks.map(({ node: labWork }, index) => {
          // Only pass labFeedbackUrl to the first item, others get undefined
          // such that multiple lab reqs cannot be actioned upon at once
          const feedbackUrl = index === 0 ? labFeedbackUrl : undefined;
          return (
            <LabListItem
              key={labWork.id}
              labWork={labWork as LabWork}
              supportLink={supportLink}
              labFeedbackUrl={feedbackUrl}
              isNew={isNew}
            />
          );
        })}
      </List>
    </div>
  );
};
